/**
 * Urgent Liquidity Hooks
 * TanStack Query hooks for urgent liquidity settings
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    getUrgentLiquiditySettings,
    updateUrgentLiquiditySettings,
    createRepaymentRange,
    updateRepaymentRange,
    deleteRepaymentRange,
    bulkCreateRepaymentRanges,
} from '@/services/urgent-liquidity.service'
import type {
    UrgentLiquiditySettings,
    UpdateUrgentLiquiditySettingsRequest,
    RepaymentRange,
    CreateRepaymentRangeRequest,
    UpdateRepaymentRangeRequest,
    BulkRepaymentRangesRequest,
} from '@/types/urgent-liquidity'

export const useUrgentLiquiditySettings = () => {
    return useQuery<UrgentLiquiditySettings>({
        queryKey: ['urgent-liquidity-settings'],
        queryFn: getUrgentLiquiditySettings,
        staleTime: 1000 * 60 * 2,
    })
}

export const useUpdateUrgentLiquiditySettings = () => {
    const queryClient = useQueryClient()

    return useMutation<
        UrgentLiquiditySettings,
        unknown,
        UpdateUrgentLiquiditySettingsRequest
    >({
        mutationFn: data => updateUrgentLiquiditySettings(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['urgent-liquidity-settings'],
            })
        },
    })
}

export const useCreateRepaymentRange = () => {
    const queryClient = useQueryClient()

    return useMutation<RepaymentRange, unknown, CreateRepaymentRangeRequest>({
        mutationFn: data => createRepaymentRange(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['urgent-liquidity-settings'],
            })
        },
    })
}

export const useUpdateRepaymentRange = () => {
    const queryClient = useQueryClient()

    return useMutation<
        RepaymentRange,
        unknown,
        { rangeId: string; data: UpdateRepaymentRangeRequest }
    >({
        mutationFn: ({ rangeId, data }) => updateRepaymentRange(rangeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['urgent-liquidity-settings'],
            })
        },
    })
}

export const useDeleteRepaymentRange = () => {
    const queryClient = useQueryClient()

    return useMutation<void, unknown, string>({
        mutationFn: (rangeId: string) => deleteRepaymentRange(rangeId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['urgent-liquidity-settings'],
            })
        },
    })
}

export const useBulkCreateRepaymentRanges = () => {
    const queryClient = useQueryClient()

    return useMutation<RepaymentRange[], unknown, BulkRepaymentRangesRequest>({
        mutationFn: data => bulkCreateRepaymentRanges(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['urgent-liquidity-settings'],
            })
        },
    })
}
